import { useState } from "react";
import { toast } from "sonner";
import { Truck } from "lucide-react";
import api, { fmtErr } from "@/lib/api";
import { useFetch, PageHeader, Loading, EmptyState, ErrorState, SectionCard, StatusChip, Paginator, fmtDate, fmtINR, ExportButton } from "@/lib/ui";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const TABS = ["READY_FOR_PICKUP", "ASSIGNED", "OUT_FOR_DELIVERY", "DELIVERED"];

export default function Delivery() {
  const [status, setStatus] = useState("READY_FOR_PICKUP");
  const [page, setPage] = useState(1);
  const limit = 20;
  const path = `/admin/orders?page=${page}&limit=${limit}&status=${status}`;
  const { data, loading, error, refetch } = useFetch(path, [path]);
  const partners = useFetch("/admin/users?role=delivery_partner&limit=100", []);
  const [assign, setAssign] = useState(null);
  const [partnerId, setPartnerId] = useState("");
  const [busy, setBusy] = useState(false);

  const doAssign = async () => {
    if (!partnerId) return;
    setBusy(true);
    try { await api.post(`/admin/orders/${assign.id}/assign`, { partner_id: partnerId }); toast.success("Delivery partner assigned"); setAssign(null); setPartnerId(""); refetch(); }
    catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title="Delivery Management" subtitle="Assign partners and track deliveries"
        actions={<ExportButton filename="sonu-delivery-payouts.csv" rows={data?.items} testid="export-delivery-btn"
          columns={[{ key: "order_number", label: "Order #" }, { key: "total", label: "Order Total", get: (r) => r.pricing?.total }, { key: "delivery_charge", label: "Delivery Charge", get: (r) => r.pricing?.delivery_charge }, { key: "delivery_mode", label: "Mode" }, { key: "distance_km", label: "Distance km" }, { key: "assigned_partner_id", label: "Partner ID" }, { key: "status", label: "Status" }, { key: "created_at", label: "Placed" }]} />} />
      <Tabs value={status} onValueChange={(v) => { setStatus(v); setPage(1); }} className="mb-4">
        <TabsList className="flex-wrap h-auto" data-testid="delivery-tabs">
          {TABS.map((s) => <TabsTrigger key={s} value={s} data-testid={`delivery-tab-${s}`}>{s.replace(/_/g, " ")}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      <SectionCard className="overflow-hidden">
        {loading ? <Loading /> : error ? <ErrorState error={error} onRetry={refetch} /> :
          !data?.items?.length ? <EmptyState label={`No ${status.replace(/_/g, " ").toLowerCase()} orders`} /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="delivery-table">
                <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                  <tr><th className="px-4 py-2.5 font-semibold">Order #</th><th className="px-4 py-2.5 font-semibold">Total</th><th className="px-4 py-2.5 font-semibold">Delivery</th><th className="px-4 py-2.5 font-semibold">Distance</th><th className="px-4 py-2.5 font-semibold">Partner</th><th className="px-4 py-2.5 font-semibold">Placed</th><th className="px-4 py-2.5"></th></tr>
                </thead>
                <tbody className="divide-y">
                  {data.items.map((o) => (
                    <tr key={o.id} className="hover:bg-muted/30" data-testid={`delivery-row-${o.id}`}>
                      <td className="px-4 py-2.5 font-mono text-xs">{o.order_number}</td>
                      <td className="px-4 py-2.5 tnum">{fmtINR(o.pricing?.total)}</td>
                      <td className="px-4 py-2.5"><span className="text-xs px-2 py-0.5 rounded bg-muted">{o.delivery_mode}</span></td>
                      <td className="px-4 py-2.5 tnum text-xs">{o.distance_km ?? "—"} km</td>
                      <td className="px-4 py-2.5 text-xs font-mono">{o.assigned_partner_id ? o.assigned_partner_id.slice(-6) : "—"}</td>
                      <td className="px-4 py-2.5 text-muted-foreground text-xs">{fmtDate(o.created_at)}</td>
                      <td className="px-4 py-2.5 text-right">
                        {["READY_FOR_PICKUP", "ASSIGNED"].includes(o.status) &&
                          <Button size="sm" variant="outline" onClick={() => setAssign(o)} data-testid={`assign-btn-${o.id}`}><Truck className="h-3.5 w-3.5 mr-1.5" /> Assign</Button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </SectionCard>
      <Paginator page={page} total={data?.total} limit={limit} onPage={setPage} />

      <Dialog open={!!assign} onOpenChange={(o) => { if (!o) { setAssign(null); setPartnerId(""); } }}>
        <DialogContent data-testid="assign-dialog">
          <DialogHeader><DialogTitle>Assign partner · {assign?.order_number}</DialogTitle></DialogHeader>
          {partners.loading ? <Loading /> : !partners.data?.items?.length ? <EmptyState label="No delivery partners registered" /> : (
            <Select value={partnerId} onValueChange={setPartnerId}>
              <SelectTrigger data-testid="assign-partner-select"><SelectValue placeholder="Select a delivery partner" /></SelectTrigger>
              <SelectContent>
                {partners.data.items.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.phone}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setAssign(null)}>Cancel</Button><Button onClick={doAssign} disabled={busy || !partnerId} data-testid="assign-confirm-btn">{busy ? "Assigning…" : "Assign"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
